import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Phone, Globe, EnvelopeSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";
import { errMsg, webUrl } from "./savShared";

const EMPTY = { name: "", phone: "", email: "", website: "", contact_name: "", notes: "" };

export function SupplierDialog({ open, supplier, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) { setF(supplier ? { ...EMPTY, ...supplier } : EMPTY); } }, [open, supplier]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nom du fournisseur requis"); return; }
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] || ""]));
    try {
      const r = supplier?.id ? await api.put(`/sav/suppliers/${supplier.id}`, body) : await api.post("/sav/suppliers", body);
      toast.success("Fournisseur enregistré"); onSaved?.(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, props = {}) => (
    <div><Label className="text-xs">{label}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`supplier-${k}`} {...props} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="supplier-dialog">
        <DialogHeader><DialogTitle>{supplier?.id ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{field("name", "Nom *")}</div>
          {field("phone", "Téléphone SAV", { type: "tel" })}
          {field("email", "Email SAV", { type: "email" })}
          {field("website", "Site internet / portail RMA")}
          {field("contact_name", "Contact")}
          <div className="sm:col-span-2"><Label className="text-xs">Notes (horaires, procédure…)</Label>
            <Textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="supplier-notes" /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annuler</Button><Button onClick={save} data-testid="supplier-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SavSuppliers({ isAdmin, refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/sav/suppliers").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [refreshKey]);
  const del = async (s) => {
    if (!window.confirm(`Supprimer ${s.name} ?`)) return;
    try { await api.delete(`/sav/suppliers/${s.id}`); toast.success("Supprimé"); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="sav-suppliers">
      <div className="mb-3 flex justify-end"><Button size="sm" onClick={() => setEdit({})} data-testid="supplier-new"><Plus size={14} className="mr-1" />Nouveau fournisseur</Button></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border bg-card p-4" data-testid={`supplier-card-${s.id}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1"><div className="font-semibold truncate">{s.name}</div>{s.contact_name && <div className="text-xs text-muted-foreground">{s.contact_name}</div>}</div>
              <span className="text-[11px] text-muted-foreground">{s.case_count} dossier(s)</span>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {s.phone && <a href={`tel:${s.phone}`} className="flex items-center gap-2 hover:underline" data-testid={`supplier-phone-${s.id}`}><Phone size={14} />{s.phone}</a>}
              {s.email && <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:underline truncate"><EnvelopeSimple size={14} />{s.email}</a>}
              {s.website && <a href={webUrl(s.website)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline truncate"><Globe size={14} />{s.website}</a>}
              {s.notes && <p className="whitespace-pre-line text-xs text-muted-foreground">{s.notes}</p>}
            </div>
            <div className="mt-3 flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEdit(s)} data-testid={`supplier-edit-${s.id}`}><PencilSimple size={14} className="mr-1" />Modifier</Button>
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(s)} data-testid={`supplier-delete-${s.id}`}><Trash size={14} className="mr-1" />Supprimer</Button>}
            </div>
          </div>
        ))}
        {!items.length && <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground sm:col-span-2 lg:col-span-3" data-testid="suppliers-empty">Aucun fournisseur. Ajoutez-en un pour le choisir dans vos dossiers.</div>}
      </div>
      <SupplierDialog open={!!edit} supplier={edit} onClose={() => setEdit(null)} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}
